import type { ListboxProps } from '@headlessui/react';
import { Listbox } from '@headlessui/react';
import type { Replace } from '@weft/utils';
import { isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import classNames from 'classnames';
import { useCallback } from 'react';
import type { FieldPath, Path, FieldPathValue, FieldValues, PathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { ArrowIcon } from '../../icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../icons/ArrowIcon/types/arrow-icon-direction';

type ListboxDropdownInput<Schema extends FieldValues> = Replace<ListboxProps<React.ElementType, FieldPathValue<Schema, FieldPath<Schema>>, unknown>, 'name', FieldPath<Schema>>;

export type DropdownProps<Schema extends FieldValues, Value = FieldPathValue<Schema, FieldPath<Schema>>> = ListboxDropdownInput<Schema> & {
  title: string;
  items: Value[];
  parseItemToString?: (item: Value) => string;
  errorMessage?: string;
  listboxClassName?: string;
  optionsClassName?: string;
  inputClassName?: string;
};

export const Dropdown = <Schema extends FieldValues>({ items, className, title, parseItemToString, listboxClassName, optionsClassName, inputClassName, ...props }: DropdownProps<Schema>) => {
  const getDisplayValue = useCallback((item: PathValue<Schema, Path<Schema>>) => {
    if (isNotNullOrUndefined(item)) {
      return parseItemToString?.(item) ?? (typeof item === 'string' ? item : '');
    }
  }, [parseItemToString]);

  return (
    <div className={className}>
      <p className="mb-2 text-sm text-label">{title}</p>
      <Listbox
        {...props}
        as='div'
        className={twMerge('relative w-full bg-white', listboxClassName)}
      >
        {({ value, open }) => (
          <>
            <Listbox.Button className='h-full w-full cursor-pointer bg-transparent'>
              <div className='flex items-center justify-between space-x-2.5'>
                <span className={twMerge('min-h-6 text-xl font-medium text-black', inputClassName)}>{getDisplayValue(value)}</span>
                <ArrowIcon
                  className={classNames('text-black', { 'text-style': open })}
                  direction={ARROW_ICON_DIRECTIONS.DOWN}
                />
              </div>
            </Listbox.Button>
            <Listbox.Options className={twMerge('absolute left-0 z-10 mt-1 w-full text-center', optionsClassName)}>
              {items.map((item, index) => (
                <Listbox.Option
                  key={index}
                  value={item}
                  className="cursor-pointer"
                >
                  <div className="bg-white py-2 shadow-sm hover:bg-input" >{getDisplayValue(item)}</div>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </>
        )}
      </Listbox>
    </div>
  );
};

export function useDropdownChangeHandler<Schema extends FieldValues>() {
  const { resetField, setValue } = useFormContext<Schema>();

  return useCallback((key: FieldPath<Schema>) => {
    return <Value extends FieldPathValue<Schema, typeof key>>(value: Value) => {
      if (isNullOrUndefined(value)) {
        resetField(key, { keepDirty: true });
      } else {
        setValue(key, value, { shouldDirty: true });
      }
    };
  }, []);
}
